import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { DataLayer } from '../lib/dataLayer';
import { Database } from '../types/database';
import { useAuth } from '../contexts/AuthContext';

type Lesson = Database['public']['Tables']['lessons']['Row'];
type Card = Database['public']['Tables']['cards']['Row'];

interface LessonViewerProps {
  lesson: Lesson;
  onEdit?: () => void;
  onBack: () => void;
  onStartStudy?: (cards: Card[]) => void;
}

export default function LessonViewer({ lesson, onEdit, onBack, onStartStudy }: LessonViewerProps) {
  const { user, profile } = useAuth();
  const [lessonCards, setLessonCards] = useState<Card[]>([]);
  const [progress, setProgress] = useState<Database['public']['Tables']['user_lesson_progress']['Row'] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLessonData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lesson.id, user?.id]);

  const loadLessonData = async () => {
    try {
      setLoading(true);
      
      // Load lesson cards
      const cards = await DataLayer.getLessonCards(lesson.id);
      setLessonCards(cards);

      // Load user progress
      if (user) {
        const userProgress = await DataLayer.getUserLessonProgress(user.id, lesson.id);
        setProgress(userProgress);

        // Mark as accessed
        if (!userProgress || userProgress.status === 'not_started') {
          await DataLayer.updateUserLessonProgress(user.id, lesson.id, 'in_progress');
        }
      }
    } catch (error) {
      console.error('Failed to load lesson data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteLesson = async () => {
    if (!user) return;
    
    try {
      await DataLayer.updateUserLessonProgress(
        user.id, 
        lesson.id, 
        'completed',
        new Date().toISOString()
      );
      setProgress(prev => prev ? { ...prev, status: 'completed' } : null);
    } catch (error) {
      console.error('Failed to mark lesson as complete:', error);
    }
  };

  const getProgressBadge = () => {
    if (!progress) return null;

    switch (progress.status) {
      case 'completed':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            Completed
          </span>
        );
      case 'in_progress':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
            </svg>
            In Progress
          </span>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Loading lesson...</div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={onBack}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Lessons
        </button>

        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{lesson.title}</h1>
            {lesson.description && (
              <p className="text-gray-600 mb-4">{lesson.description}</p>
            )}
            <div className="flex items-center gap-4">
              {lesson.level && (
                <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-md text-sm font-medium">
                  {lesson.level}
                </span>
              )}
              {getProgressBadge()}
              {lesson.tags && lesson.tags.length > 0 && (
                <div className="flex gap-2">
                  {lesson.tags.map((tag, index) => (
                    <span key={index} className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-sm">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-2">
            {profile?.is_admin && onEdit && (
              <button
                onClick={onEdit}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              >
                Edit
              </button>
            )}
            {lessonCards.length > 0 && onStartStudy && (
              <button
                onClick={() => onStartStudy(lessonCards)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Study Cards ({lessonCards.length})
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="prose prose-lg max-w-none bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            // Style tables
            table: ({ children }) => (
              <table className="min-w-full divide-y divide-gray-300">{children}</table>
            ),
            thead: ({ children }) => (
              <thead className="bg-gray-50">{children}</thead>
            ),
            tbody: ({ children }) => (
              <tbody className="divide-y divide-gray-200 bg-white">{children}</tbody>
            ),
            th: ({ children }) => (
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                {children}
              </th>
            ),
            td: ({ children }) => (
              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                {children}
              </td>
            ),
            // Style code blocks
            code: ({ children, className, ...props }: any) => {
              const isInline = !className || !className.includes('language-');
              if (isInline) {
                return (
                  <code className="px-1.5 py-0.5 bg-gray-100 text-red-600 rounded text-sm" {...props}>
                    {children}
                  </code>
                );
              }
              return (
                <pre className="block p-4 bg-gray-50 rounded-lg overflow-x-auto">
                  <code {...props}>
                    {children}
                  </code>
                </pre>
              );
            },
            // Style headings
            h1: ({ children }) => (
              <h1 className="text-3xl font-bold text-gray-900 mt-8 mb-4">{children}</h1>
            ),
            h2: ({ children }) => (
              <h2 className="text-2xl font-semibold text-gray-900 mt-6 mb-3">{children}</h2>
            ),
            h3: ({ children }) => (
              <h3 className="text-xl font-semibold text-gray-900 mt-4 mb-2">{children}</h3>
            ),
            // Style lists
            ul: ({ children }) => (
              <ul className="list-disc pl-6 space-y-2">{children}</ul>
            ),
            ol: ({ children }) => (
              <ol className="list-decimal pl-6 space-y-2">{children}</ol>
            ),
            // Style blockquotes
            blockquote: ({ children }) => (
              <blockquote className="border-l-4 border-gray-300 pl-4 italic text-gray-700">
                {children}
              </blockquote>
            ),
          }}
        >
          {lesson.content}
        </ReactMarkdown>
      </div>

      {/* Actions */}
      {progress?.status !== 'completed' && (
        <div className="mt-8 flex justify-end">
          <button
            onClick={handleCompleteLesson}
            className="px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors font-medium"
          >
            Mark as Complete
          </button>
        </div>
      )}
    </div>
  );
}